import { useQuery } from "@tanstack/react-query";
import { Undo2 } from "lucide-react";
import { api } from "../../shared/api/client";
import type { ArchiveGroupView, WorkItemSummary } from "../../shared/types/api";
import { Button, Field, Select, type Column } from "../../shared/ui";

/**
 * The batches of finished work that have been filed away.
 *
 * <p>Read by every list that offers the chooser, and again by the drawer that files work into
 * one, so they share a key and a single fetch.
 */
export function useArchiveGroups(projectId: string | undefined) {
  return useQuery({
    queryKey: ["archive-groups", projectId],
    queryFn: () =>
      api.get<ArchiveGroupView[]>(`/api/v1/work-archive-groups?projectId=${projectId}`),
    enabled: Boolean(projectId),
  });
}

/** What is in one group — the only listing in the application that shows archived work. */
export function useArchivedItems(groupId: string) {
  return useQuery({
    queryKey: ["archive-group-items", groupId],
    queryFn: () => api.get<WorkItemSummary[]>(`/api/v1/work-archive-groups/${groupId}/items`),
    enabled: groupId !== "",
  });
}

export function reopenArchived(itemId: string): Promise<unknown> {
  return api.post(`/api/v1/work-archive-groups/items/${itemId}/reopen`, {});
}

/**
 * The chooser, sitting among the other filters because that is what it is.
 *
 * <p>Renders nothing at all on a project with no groups. An empty chooser is a question about
 * something that does not exist yet, and it would take width from the filters that do.
 *
 * <p>The count rides on each name: a list of names alone says nothing about which group holds
 * the forty things somebody is looking for.
 */
export function ArchiveGroupField({
  groups,
  value,
  onChange,
}: {
  groups: ArchiveGroupView[];
  value: string;
  onChange: (groupId: string) => void;
}) {
  if (groups.length === 0) {
    return null;
  }
  return (
    <div className="w-36 shrink-0">
      <Field label="Filed away">
        <Select value={value} onChange={(event) => onChange(event.target.value)}>
          <option value="">Live work</option>
          {groups.map((group) => (
            <option key={group.id} value={group.id}>
              {group.name} ({group.itemCount})
            </option>
          ))}
        </Select>
      </Field>
    </div>
  );
}

/**
 * The way back out, offered on every row of a group.
 *
 * <p>Last column, and only while a group is being read. On the live list it would be an action
 * with nothing to undo.
 */
export function reopenColumn(
  reopen: (itemId: string) => void,
  busyId: string | null,
): Column<WorkItemSummary> {
  return {
    key: "reopen",
    header: "",
    className: "w-28 text-right",
    render: (item) => (
      <Button
        size="sm"
        variant="secondary"
        icon={<Undo2 className="h-3.5 w-3.5" />}
        loading={busyId === item.id}
        /* The row opens the item, so the button must not also do that. */
        onClick={(event) => {
          event.stopPropagation();
          reopen(item.id);
        }}
      >
        Reopen
      </Button>
    ),
  };
}
